import { useAuth0 } from '@auth0/auth0-react';
import {
  useAddress,
  useCreateAddress,
  useCreateOrganisationSequence,
  useCreateSite,
  useDeleteOrganisationLogo,
  useOrganisation,
  useOrganisationBankAccounts,
  useOrganisationSequences,
  useUpdateAddress,
  useUpdateOrganisation,
  useUpdateOrganisationAdditionalCurrencies,
  useUpdateOrganisationSequence,
  useUpdateOrganisationTmsOrderReferenceLabels,
  useUploadOrganisationLogo,
} from '@my/directory-api/hooks';
import type {
  AccountingSystemValue,
  PaymentTermBasisValue,
  OrganisationSequence,
  SequenceTypeValue,
} from '@my/directory-api';
import {
  AddressInlineEditor,
  Button,
  ErrorBanner,
  FormSection,
  Loading,
  LogoUploader,
  PageHeader,
  RegionalSettingsInlineEditor,
  SelectInput,
  showToast,
  Switch,
  TextInput,
  type SelectInputOption,
} from '@my/ui';
import {
  applyCountryDefaults,
  COUNTRIES,
  CURRENCIES,
  DATE_PATTERNS,
  LANGUAGES,
  TIME_PATTERNS,
  TIMEZONES,
} from '@my/platform/options';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DirectoryHistoryButton } from '@/components/HistoryButtons';
import { describePaymentTerms, PAYMENT_TERM_BASIS_LABEL } from '@/utils/paymentTerms';
import {
  EDITOR_SEQUENCE_TYPES,
  SequenceConfigEditor,
  type SequenceFormFields,
} from './SequenceConfigEditor';
import {
  OrderReferenceLabelsEditor,
  ORDER_REF_LABELS_MIN_ROWS,
  parseOrderReferenceLabels,
  serialiseOrderReferenceLabels,
} from './OrderReferenceLabelsEditor';
import { AdministratorsSection } from './AdministratorsSection';
import { BankAccountsSection } from './BankAccountsSection';
import { EmailSendingSection } from './EmailSendingSection';
import { ChargesAndCostsSection } from './ChargesAndCostsSection';
import { CurrenciesSection } from './CurrenciesSection';
import { AccountingCategoriesSection } from './AccountingCategoriesSection';
import { NominalCodesSection } from './NominalCodesSection';
import { TaxSchemasSection } from './TaxSchemasSection';
import { VatTreatmentSection } from './VatTreatmentSection';
import { AddNewAddressPanel } from './AddNewAddressPanel';
import { ExtraFieldsList } from './ExtraFieldsList';
import {
  EMPTY_ADDRESS_FORM,
  EMPTY_SEQUENCE_CONFIG,
  isSequenceFormPristine,
  parsePaymentTermDays,
  toAddressForm,
  toCreateAddressRequest,
  toCreateSiteRequest,
  toOrgForm,
  toSequenceConfigForm,
  toUpdateAddressRequest,
  toUpdateOrganisationRequest,
  toUpdateSequenceRequest,
  type AddressForm,
  type OrganisationForm,
} from './lib/formMappers';
import { parseAdditionalCurrencyCodes, planAdditionalCurrencies } from './lib/currencyPlanning';

// ────────────────────────────────────────────────────────────────────────────
// Dropdown options. Source-of-truth lists live in `@my/platform/options`;
// here we just shape them into the `SelectInputOption` form the UI primitives
// expect. Country labels carry the flag emoji as a small visual cue, currency
// labels include the symbol — keeps the dropdowns scannable at a glance.
// ────────────────────────────────────────────────────────────────────────────

const COUNTRY_OPTIONS: SelectInputOption[] = COUNTRIES.map((c) => ({
  value: c.isoCode,
  label: `${c.flagEmoji} ${c.name}`,
}));

const TIMEZONE_OPTIONS: SelectInputOption[] = TIMEZONES.map((z) => ({
  value: z.iana,
  label: z.label,
}));

const LANGUAGE_OPTIONS: SelectInputOption[] = LANGUAGES.map((l) => ({
  value: l.isoCode,
  label: `${l.name} (${l.nativeName})`,
}));

const TIME_PATTERN_OPTIONS: SelectInputOption[] = TIME_PATTERNS.map((t) => ({
  value: t.pattern,
  label: t.label,
}));

const DATE_PATTERN_OPTIONS: SelectInputOption[] = DATE_PATTERNS.map((d) => ({
  value: d.pattern,
  label: d.label,
}));

const CURRENCY_OPTIONS: SelectInputOption[] = CURRENCIES.map((c) => ({
  value: c.isoCode,
  label: `${c.name} (${c.isoCode})`,
}));

/**
 * `AccountingSystem` enum — the Directory API serialises enums as strings
 * (`JsonStringEnumConverter`), so the wire values match the C# member names
 * verbatim. Display labels add spacing where the C# name omits it (e.g.
 * `Sage200` → `Sage 200`).
 */
const PAYMENT_TERM_BASIS_OPTIONS: { value: PaymentTermBasisValue; label: string }[] = [
  { value: 'InvoiceDate', label: PAYMENT_TERM_BASIS_LABEL.InvoiceDate },
  { value: 'EndOfMonth', label: PAYMENT_TERM_BASIS_LABEL.EndOfMonth },
];

const ACCOUNTING_SYSTEM_OPTIONS: { value: AccountingSystemValue; label: string }[] = [
  { value: 'None', label: 'None' },
  { value: 'Xero', label: 'Xero' },
  { value: 'QuickBooks', label: 'QuickBooks' },
  { value: 'Sage', label: 'Sage' },
  { value: 'Sage200', label: 'Sage 200' },
  { value: 'Zoho', label: 'Zoho' },
  { value: 'Surf', label: 'Surf' },
  { value: 'BigRedCloud', label: 'Big Red Cloud' },
];

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

/**
 * Phase 5b — Settings/Organisation. Top "Organisation Details" panel mirrors
 * the original My.Tms.Old layout (3-column FormSection with logo, inline
 * address editor, regional settings, invoice config, account system, vat
 * rounding), plus the new `Registration Number` field.
 *
 * Form-state plumbing (DTO ↔ form converters) lives in `formMappers.ts`;
 * the auto-promote rule for additional currencies lives in
 * `currencyPlanning.ts`; per-section editors (`AddNewAddressPanel`,
 * `ExtraFieldsList`, plus the bottom-row cards) are each in their own file.
 * This page is pure orchestration.
 */
export function OrganisationPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['organisation', 'common']);

  const orgQuery = useOrganisation(orgId);
  const updateOrgMutation = useUpdateOrganisation();
  const updateAdditionalCurrenciesMutation = useUpdateOrganisationAdditionalCurrencies();
  // Bank accounts list — read here so the auto-promote logic only kicks in
  // when the OLD base actually has dependents to preserve. React Query
  // dedupes the same hook in BankAccountsSection / CurrenciesSection.
  const bankAccountsQuery = useOrganisationBankAccounts(orgId);
  const uploadLogoMutation = useUploadOrganisationLogo();
  const deleteLogoMutation = useDeleteOrganisationLogo();

  const addressId = orgQuery.data?.mainOfficeAddressId ?? null;
  const addressQuery = useAddress(addressId);
  const createAddressMutation = useCreateAddress();
  const updateAddressMutation = useUpdateAddress();
  const createSiteMutation = useCreateSite();

  const sequencesQuery = useOrganisationSequences(orgId);
  const createSequenceMutation = useCreateOrganisationSequence();
  const updateSequenceMutation = useUpdateOrganisationSequence();

  const updateTmsOrderReferenceLabelsMutation = useUpdateOrganisationTmsOrderReferenceLabels();
  const { sequencesByType, serverLockedByType } = useMemo(() => {
    const byType = {} as Partial<Record<SequenceTypeValue, OrganisationSequence>>;
    const locked = {} as Record<SequenceTypeValue, boolean>;
    for (const type of EDITOR_SEQUENCE_TYPES) {
      const row = sequencesQuery.data?.find((s) => s.type === type);
      byType[type] = row;
      locked[type] = row?.isLocked ?? false;
    }
    return { sequencesByType: byType, serverLockedByType: locked };
  }, [sequencesQuery.data]);

  const [orgForm, setOrgForm] = useState<OrganisationForm | null>(null);
  const [addressForm, setAddressForm] = useState<AddressForm>(EMPTY_ADDRESS_FORM);
  const [sequenceConfigForm, setSequenceConfigForm] = useState(EMPTY_SEQUENCE_CONFIG);
  const [orderReferenceLabels, setOrderReferenceLabels] = useState<string[]>(() =>
    Array.from({ length: ORDER_REF_LABELS_MIN_ROWS }, () => ''),
  );
  const [touchedSequenceTypes, setTouchedSequenceTypes] = useState<Set<SequenceTypeValue>>(
    () => new Set(),
  );
  const [addressSubmitted, setAddressSubmitted] = useState(false);

  useEffect(() => {
    if (orgQuery.data) setOrgForm(toOrgForm(orgQuery.data));
  }, [orgQuery.data, orgQuery.dataUpdatedAt]);

  useEffect(() => {
    if (orgQuery.data) {
      setOrderReferenceLabels(parseOrderReferenceLabels(orgQuery.data.tmsOrderReferenceLabelsJson));
    }
  }, [orgQuery.data, orgQuery.dataUpdatedAt]);

  useEffect(() => {
    if (addressQuery.data) setAddressForm(toAddressForm(addressQuery.data));
    else if (!addressId) setAddressForm(EMPTY_ADDRESS_FORM);
  }, [addressQuery.data, addressQuery.dataUpdatedAt, addressId]);

  useEffect(() => {
    if (!sequencesQuery.data) return;
    setSequenceConfigForm(toSequenceConfigForm(sequencesQuery.data));
    setTouchedSequenceTypes(new Set());
  }, [sequencesQuery.dataUpdatedAt, sequencesQuery.data]);

  /**
   * Editor change handler — replaces the row for `type` wholesale and
   * marks it touched so the save loop knows to POST it if missing.
   */
  const handleSequenceChange = (type: SequenceTypeValue, next: SequenceFormFields) => {
    setSequenceConfigForm((prev) => ({ ...prev, [type]: next }));
    setTouchedSequenceTypes((prev) => {
      if (prev.has(type)) return prev;
      const updated = new Set(prev);
      updated.add(type);
      return updated;
    });
  };

  const setOrgField = <K extends keyof OrganisationForm>(key: K, value: OrganisationForm[K]) =>
    setOrgForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const effectiveAdditionalCodes = useMemo<string[]>(() => {
    if (!orgForm || !orgQuery.data) return [];
    return planAdditionalCurrencies({
      serverBase: orgQuery.data.currencyIsoCode,
      formBase: orgForm.currencyIsoCode,
      serverAdditionalsRaw: orgQuery.data.additionalCurrencyIsoCodes,
      bankAccounts: bankAccountsQuery.data ?? [],
    });
  }, [orgForm, orgQuery.data, bankAccountsQuery.data]);

  const enabledCurrencyOptions = useMemo<SelectInputOption[]>(() => {
    if (!orgForm) return [];
    const codes = new Set<string>();
    if (orgForm.currencyIsoCode) codes.add(orgForm.currencyIsoCode.toUpperCase());
    for (const c of effectiveAdditionalCodes) codes.add(c);
    return Array.from(codes).map((code) => {
      const currency = CURRENCIES.find((c) => c.isoCode === code);
      return {
        value: code,
        label: currency ? `${currency.name} (${currency.isoCode})` : code,
      };
    });
  }, [orgForm, effectiveAdditionalCodes]);

  const handleAddressChange = (next: AddressForm) => {
    const previousCountry = addressForm.countryIsoCode;
    const newCountry = next.countryIsoCode;

    if (newCountry && newCountry !== previousCountry) {
      const patches = applyCountryDefaults({
        newCountry,
        previousCountry: previousCountry || null,
        current: orgForm
          ? {
              languageCode: orgForm.languageCode,
              currencyIsoCode: orgForm.currencyIsoCode,
              operationalTimeZone: orgForm.operationalTimeZone,
              datePattern: orgForm.datePattern,
              timePattern: orgForm.timePattern,
            }
          : {},
      });
      if (Object.keys(patches).length > 0) {
        setOrgForm((prev) => (prev ? { ...prev, ...patches } : prev));
      }
    }

    setAddressForm(next);
  };

  const saveSequence = async (
    targetOrgId: string,
    type: SequenceTypeValue,
    form: SequenceFormFields,
    existing: OrganisationSequence | undefined,
  ) => {
    // YearMonth doesn't use a typed prefix — the BE renders `YYMM-` from
    // the reset stamps. Plain still requires a prefix; if blank, skip
    // (nothing to seed/update).
    const isYearMonth = form.format === 'YearMonth';
    const prefix = isYearMonth ? '' : form.prefix.trim();
    if (!isYearMonth && !prefix) return;
    const parsedCurrent = parseInt(form.currentNumber, 10);
    const currentNumber = Number.isFinite(parsedCurrent) ? Math.max(0, parsedCurrent) : 0;

    if (!existing) {
      await createSequenceMutation.mutateAsync({
        organisationId: targetOrgId,
        type,
        format: form.format,
        prefix,
        currentNumber,
        isLocked: form.isLocked,
      });
      return;
    }

    // Skip the PUT when nothing meaningful changed — keeps the audit log clean.
    const changed =
      existing.format !== form.format ||
      existing.prefix !== prefix ||
      (!existing.isLocked && existing.currentNumber !== currentNumber) ||
      (!existing.isLocked && form.isLocked);

    if (!changed) return;

    await updateSequenceMutation.mutateAsync({
      organisationId: targetOrgId,
      body: toUpdateSequenceRequest(existing, form),
    });
  };

  const addressHasAnyInput =
    addressForm.fullAddress.trim().length > 0 ||
    addressForm.city.trim().length > 0 ||
    addressForm.postcode.trim().length > 0;

  const addressMissingFullAddress = addressHasAnyInput && !addressForm.fullAddress.trim();
  const addressMissingPostcode = addressHasAnyInput && !addressForm.postcode.trim();
  const addressMissingCountry = addressHasAnyInput && !addressForm.countryIsoCode.trim();
  const addressMissingLocation =
    addressHasAnyInput && (addressForm.latitude == null || addressForm.longitude == null);
  const addressInvalid =
    addressMissingFullAddress ||
    addressMissingPostcode ||
    addressMissingCountry ||
    addressMissingLocation;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!orgId || !orgQuery.data || !orgForm) return;
    if (!orgForm.name.trim() || !orgForm.invoicingName.trim()) return;

    // Validate the address-create form only when there is no existing address
    // yet. The update path is validated lower down (existing-address branch).
    if (!addressId && addressInvalid) {
      setAddressSubmitted(true);
      showToast({
        type: 'error',
        message: addressMissingLocation
          ? t('organisation:validation.addressVerifyLocation')
          : t('organisation:validation.addressRequired'),
      });
      return;
    }

    try {
      let mainOfficeAddressId = orgQuery.data.mainOfficeAddressId ?? null;
      const wantsAddressCreate =
        !addressId &&
        addressForm.fullAddress.trim().length > 0 &&
        addressForm.countryIsoCode.trim().length > 0;

      if (wantsAddressCreate) {
        mainOfficeAddressId = await createAddressMutation.mutateAsync(
          toCreateAddressRequest(orgId, addressForm),
        );
        await createSiteMutation.mutateAsync(toCreateSiteRequest(orgId, mainOfficeAddressId));
      }

      await updateOrgMutation.mutateAsync(
        // The main office's country comes along: it is where the country is
        // actually edited, and the organisation's own `countryIsoCode` is what
        // drives the tax schedule and the nominal-code defaults. Without it the
        // request echoed the stored value back and the country never moved.
        toUpdateOrganisationRequest(
          orgQuery.data,
          orgForm,
          mainOfficeAddressId,
          addressForm.countryIsoCode,
        ),
      );
      const oldBase = (orgQuery.data.currencyIsoCode ?? '').toUpperCase();
      const newBase = orgForm.currencyIsoCode.trim().toUpperCase();
      if (oldBase && newBase && oldBase !== newBase) {
        const currentAdditionals = parseAdditionalCurrencyCodes(
          orgQuery.data.additionalCurrencyIsoCodes,
          oldBase,
        );
        const nextList = planAdditionalCurrencies({
          serverBase: oldBase,
          formBase: newBase,
          serverAdditionalsRaw: orgQuery.data.additionalCurrencyIsoCodes,
          bankAccounts: bankAccountsQuery.data ?? [],
        });
        const a = [...currentAdditionals].sort().join(',');
        const b = [...nextList].sort().join(',');
        if (a !== b) {
          await updateAdditionalCurrenciesMutation.mutateAsync({
            organisationId: orgId,
            codes: nextList,
          });
        }
      }

      const desiredOrderLabelsJson = serialiseOrderReferenceLabels(orderReferenceLabels);
      const serverOrderLabelsJson = orgQuery.data.tmsOrderReferenceLabelsJson ?? null;
      if (desiredOrderLabelsJson !== serverOrderLabelsJson) {
        await updateTmsOrderReferenceLabelsMutation.mutateAsync({
          organisationId: orgId,
          labelsJson: desiredOrderLabelsJson,
        });
      }

      // 3. Update existing main-office address.
      if (addressId && addressQuery.data) {
        if (!addressForm.fullAddress.trim() || !addressForm.countryIsoCode.trim()) {
          showToast({
            type: 'error',
            message: t('organisation:validation.fullAddressAndCountryRequired'),
          });
          return;
        }
        if (!addressForm.postcode.trim()) {
          showToast({ type: 'error', message: t('organisation:validation.postcodeRequired') });
          return;
        }
        if (addressForm.latitude == null || addressForm.longitude == null) {
          showToast({
            type: 'error',
            message: t('organisation:validation.addressVerifyLocation'),
          });
          return;
        }
        await updateAddressMutation.mutateAsync(
          toUpdateAddressRequest(addressQuery.data, addressForm),
        );
      }

      for (const type of EDITOR_SEQUENCE_TYPES) {
        const existing = sequencesByType[type];
        const form = sequenceConfigForm[type];
        if (!existing && !touchedSequenceTypes.has(type)) continue;
        if (!existing && isSequenceFormPristine(form, type)) continue;
        await saveSequence(orgId, type, form, existing);
      }

      showToast({ type: 'success', message: t('organisation:toasts.saved') });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('organisation:toasts.saveFailed');
      showToast({ type: 'error', message });
    }
  };

  const isSaving =
    updateOrgMutation.isPending ||
    updateAdditionalCurrenciesMutation.isPending ||
    updateTmsOrderReferenceLabelsMutation.isPending ||
    createAddressMutation.isPending ||
    updateAddressMutation.isPending ||
    createSiteMutation.isPending ||
    createSequenceMutation.isPending ||
    updateSequenceMutation.isPending;
  const isLogoUploading = uploadLogoMutation.isPending || deleteLogoMutation.isPending;

  const handleLogoUpload = (file: File) => {
    if (!orgId) return;
    uploadLogoMutation.mutate(
      { id: orgId, file },
      {
        onSuccess: () =>
          showToast({ type: 'success', message: t('organisation:toasts.logoUpdated') }),
        onError: (err) =>
          showToast({
            type: 'error',
            message: err instanceof Error ? err.message : t('organisation:toasts.logoUploadFailed'),
          }),
      },
    );
  };

  const handleLogoClear = () => {
    if (!orgId) return;
    deleteLogoMutation.mutate(
      { id: orgId },
      {
        onSuccess: () =>
          showToast({ type: 'success', message: t('organisation:toasts.logoRemoved') }),
        onError: (err) =>
          showToast({
            type: 'error',
            message: err instanceof Error ? err.message : t('organisation:toasts.logoRemoveFailed'),
          }),
      },
    );
  };

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title={t('organisation:title')} description={t('organisation:description')}>
        {/* Covers the settings that hang off the organisation too — sequences,
            tax rates, nominals, bank accounts, document layouts, equipment
            types, carriers and routes all file their changes here. */}
        <DirectoryHistoryButton
          organisationId={orgId}
          aggregateType="Organisation"
          aggregateId={orgId}
          subject={orgQuery.data?.name ?? 'Organisation'}
          variant="subtle"
        />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title={t('common:errors.noOrgLinkedTitle')}
            message={t('common:errors.noOrgLinkedMessage')}
          />
        )}

        {orgQuery.isError && (
          <ErrorBanner
            title={t('organisation:errors.loadFailed')}
            message={orgQuery.error.message}
          />
        )}

        {orgQuery.isPending && orgId && (
          <div className="rounded-md border border-slate-200 bg-white">
            <Loading fullHeight label={t('organisation:loading.org')} />
          </div>
        )}

        {orgQuery.isSuccess && orgForm && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <FormSection title={t('organisation:formSection.details')} columns={3}>
              {/* ── Column 1 ── Logo, Name, Address, Regional ─ */}
              <div className="space-y-3 text-sm">
                <LogoUploader
                  label={t('organisation:fields.logo')}
                  value={orgQuery.data?.logoUrl ?? null}
                  onUpload={handleLogoUpload}
                  onClear={handleLogoClear}
                  isUploading={isLogoUploading}
                />
                <TextInput
                  label={t('organisation:fields.name')}
                  name="name"
                  value={orgForm.name}
                  onChange={(e) => setOrgField('name', e.target.value)}
                  error={!orgForm.name.trim()}
                  errorMessage={t('organisation:validation.nameRequired')}
                />

                {addressQuery.data ? (
                  <AddressInlineEditor
                    label={t('organisation:fields.address')}
                    value={addressForm}
                    onChange={handleAddressChange}
                    countryOptions={COUNTRY_OPTIONS}
                    enablePlacesSearch
                    hasCodeField
                  />
                ) : addressId && addressQuery.isPending ? (
                  <p className="text-xs text-slate-500">{t('organisation:loading.address')}</p>
                ) : addressId && addressQuery.isError ? (
                  <p className="text-xs text-red-600">
                    {addressQuery.error?.message ?? t('organisation:errors.loadAddressFailed')}
                  </p>
                ) : (
                  <AddNewAddressPanel
                    value={addressForm}
                    onChange={handleAddressChange}
                    countryOptions={COUNTRY_OPTIONS}
                    showErrors={addressSubmitted}
                    missingFullAddress={addressMissingFullAddress}
                    missingPostcode={addressMissingPostcode}
                    missingCountry={addressMissingCountry}
                  />
                )}

                <RegionalSettingsInlineEditor
                  label={t('organisation:fields.regionalSettings')}
                  value={{
                    operationalTimeZone: orgForm.operationalTimeZone,
                    languageCode: orgForm.languageCode,
                    timePattern: orgForm.timePattern,
                    datePattern: orgForm.datePattern,
                  }}
                  onChange={(next) => setOrgForm((prev) => (prev ? { ...prev, ...next } : prev))}
                  timeZoneOptions={TIMEZONE_OPTIONS}
                  languageOptions={LANGUAGE_OPTIONS}
                  timePatternOptions={TIME_PATTERN_OPTIONS}
                  datePatternOptions={DATE_PATTERN_OPTIONS}
                />
              </div>

              {/* ── Column 2 ── Invoicing Name, Tax / Reg Number, Invoice Config, Base Currency ── */}
              <div className="space-y-3 text-sm">
                <TextInput
                  label={t('organisation:fields.invoicingName')}
                  name="invoicingName"
                  value={orgForm.invoicingName}
                  onChange={(e) => setOrgField('invoicingName', e.target.value)}
                  error={!orgForm.invoicingName.trim()}
                  errorMessage={t('organisation:validation.invoicingNameRequired')}
                />

                <TextInput
                  label={t('organisation:fields.taxNumber')}
                  name="taxNumber"
                  value={orgForm.taxNumber}
                  onChange={(e) => setOrgField('taxNumber', e.target.value)}
                />

                <TextInput
                  label={t('organisation:fields.registrationNumber')}
                  name="registrationNumber"
                  value={orgForm.registrationNumber}
                  onChange={(e) => setOrgField('registrationNumber', e.target.value)}
                />

                <SequenceConfigEditor
                  value={sequenceConfigForm}
                  onChange={handleSequenceChange}
                  serverLocked={serverLockedByType}
                  loading={sequencesQuery.isPending}
                />
                {sequencesQuery.isError && (
                  <p className="text-xs text-red-600">
                    {t('organisation:errors.loadInvoiceFailed', {
                      message: sequencesQuery.error.message,
                    })}
                  </p>
                )}

                <SelectInput
                  label={t('organisation:fields.baseCurrency')}
                  name="currencyIsoCode"
                  value={orgForm.currencyIsoCode}
                  onChange={(e) => setOrgField('currencyIsoCode', e.target.value)}
                  options={CURRENCY_OPTIONS}
                />
              </div>

              {/* ── Column 3 ── Account System, VAT Rounding, Custom Fields ── */}
              <div className="space-y-3 text-sm">
                <TextInput
                  label={'Network ID'}
                  name="networkId"
                  value={orgForm.networkId}
                  readOnly
                  disabled
                  onChange={(e) => setOrgField('networkId', e.target.value)}
                />

                <SelectInput
                  label={t('organisation:fields.accountSystem')}
                  name="accountingSystem"
                  value={orgForm.accountingSystem}
                  onChange={(e) =>
                    setOrgField('accountingSystem', e.target.value as AccountingSystemValue)
                  }
                  options={ACCOUNTING_SYSTEM_OPTIONS}
                />

                <Switch
                  label={t('organisation:fields.vatRoundingMethod')}
                  name="vatRoundingMethod"
                  // The switch shows labels matching Old. Wire values are strings.
                  checked={orgForm.vatRoundingMethod === 'Item'}
                  enabledLabel={t('organisation:fields.vatRoundingItem')}
                  disabledLabel={t('organisation:fields.vatRoundingTotal')}
                  onChange={(e) =>
                    setOrgField('vatRoundingMethod', e.currentTarget.checked ? 'Item' : 'Total')
                  }
                />

                {/* Accounting categories: a code per line of business sent to
                    the accounts package beside the nominal. Sits with the VAT
                    rounding switch because both decide how lines reach the
                    accounts. Only the switch for now; the category list and
                    where each line gets its code come next. */}
                <Switch
                  label={t('organisation:fields.useAccountingCategories')}
                  name="useAccountingCategories"
                  checked={orgForm.useAccountingCategories}
                  enabledLabel={t('common:states.yes')}
                  disabledLabel={t('common:states.no')}
                  onChange={(e) => setOrgField('useAccountingCategories', e.currentTarget.checked)}
                />

                {/* TMS-specific: per-org names for `Order.Reference1..N`
                    slots on the TMS Order Details page. Sits directly
                    under the VAT Rounding switch (above the custom-
                    fields list) so the column reads as "mapping
                    configuration first, freeform custom fields after."
                    Saved via its own dedicated PUT — see step 2b of
                    `handleSubmit`. */}
                <OrderReferenceLabelsEditor
                  value={orderReferenceLabels}
                  onChange={setOrderReferenceLabels}
                />

                {/* House payment terms. The customer-level field on the
                    customer page overrides this; anything left inheriting
                    follows whatever is set here, which is also what the
                    invoice builder defaults each due date from. */}
                <div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <p
                      className="col-span-1 mb-1 text-xs font-medium text-slate-600"
                      style={{ alignContent: 'space-evenly' }}
                    >
                      {t('organisation:fields.defaultPaymentTerms')}
                    </p>
                    <TextInput
                      label={t('organisation:fields.paymentTermDays')}
                      name="defaultPaymentTermDays"
                      type="number"
                      min={0}
                      value={orgForm.defaultPaymentTermDays}
                      onChange={(e) => setOrgField('defaultPaymentTermDays', e.target.value)}
                      className="w-20"
                    />
                    <SelectInput
                      label={t('organisation:fields.paymentTermBasis')}
                      name="defaultPaymentTermBasis"
                      value={orgForm.defaultPaymentTermBasis}
                      onChange={(e) =>
                        setOrgField(
                          'defaultPaymentTermBasis',
                          e.target.value as PaymentTermBasisValue,
                        )
                      }
                      options={PAYMENT_TERM_BASIS_OPTIONS}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">
                    {t('organisation:fields.paymentTermsPreview', {
                      terms: describePaymentTerms({
                        days: parsePaymentTermDays(orgForm.defaultPaymentTermDays),
                        basis: orgForm.defaultPaymentTermBasis,
                      }),
                    })}
                  </p>
                </div>

                <ExtraFieldsList
                  fields={orgForm.extraFields}
                  onChange={(next) =>
                    setOrgForm((prev) => (prev ? { ...prev, extraFields: next } : prev))
                  }
                />
              </div>

              {/* ── Footer ── tiny green tick at the bottom-right, matches Old ── */}
              <div className="flex justify-end p-0 md:col-span-3">
                <Button
                  type="submit"
                  variant="custom"
                  size="xs"
                  icon={Check}
                  ariaLabel={t('organisation:aria.save')}
                  loading={isSaving}
                  className="p-1 text-green-600 hover:bg-green-50 [&_svg]:h-3.5 [&_svg]:w-3.5"
                />
              </div>
            </FormSection>

            {/* ── Bottom row: three side-by-side cards ── */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <AdministratorsSection organisationId={orgId} />
              <CurrenciesSection
                organisationId={orgId}
                baseCurrencyIsoCode={orgForm.currencyIsoCode}
                // Pass the EFFECTIVE list (auto-promote-aware) so the card
                // previews the planned state before the org save lands.
                additionalCurrencyIsoCodes={
                  effectiveAdditionalCodes.length > 0 ? effectiveAdditionalCodes.join(',') : null
                }
              />
              <BankAccountsSection
                organisationId={orgId}
                currencyOptions={enabledCurrencyOptions}
              />
            </div>

            {/* The organisation's own mailbox for sending documents. Saves on
                its own, and the sign-in round-trips through Microsoft/Google. */}
            <EmailSendingSection
              organisationId={orgId}
              // The SAVED name — it's what documents actually go out under.
              invoicingName={orgQuery.data?.invoicingName ?? null}
            />

            <TaxSchemasSection
              organisationId={orgId}
              organisationCountryIsoCode={orgQuery.data?.countryIsoCode ?? null}
            />

            {/* Which of those schemas each sale uses — by the customer's VAT
                country and where the goods go. */}
            <VatTreatmentSection organisationId={orgId} />

            {/* Follows the switch as it is on screen, not as saved: turning
                it on shows the list straight away so categories can be set up
                before the organisation is saved. The list itself saves on its
                own, like the tax and nominal tables around it. */}
            {orgForm.useAccountingCategories && (
              <AccountingCategoriesSection organisationId={orgId} />
            )}

            <NominalCodesSection
              organisationId={orgId}
              organisationCountryIsoCode={orgQuery.data?.countryIsoCode ?? null}
            />

            <ChargesAndCostsSection
              organisationId={orgId}
              // The switch as it stands on screen, like the categories box above.
              categoriesEnabled={orgForm.useAccountingCategories}
            />
          </form>
        )}
      </main>
    </div>
  );
}
